<script setup lang="ts">
import { Info, Store } from 'lucide-vue-next'

import { getAudienceColor } from '@/composables/audiences'

import type { SelectedAudience } from '@/components/audiences/Audience.vue'

const { t } = useI18n()
const { formatNumber } = useNumber()

const showExplanations = computed(() => {
  const total = props.targetsCount
  const stores = props.storesContactsCount

  if (total === null || total === undefined) return false
  if (stores === null || stores === undefined) return false

  return total !== stores
})

const props = withDefaults(
  defineProps<{
    rules: object
    targetsCount: number
    selectedAudience: SelectedAudience
    storesContactsCount: number
    storesCount: number
    loading: boolean
  }>(),
  {
    rules: () => ({}),
    targetsCount: 0,
    storesContactsCount: 0,
    storesCount: 0,
    loading: true
  }
)

const stores = computed(() => props.selectedAudience?.stores || [])
const availableLists = computed(
  () =>
    props.selectedAudience?.availableLists
      .filter((list) => props.selectedAudience?.lists.includes(list.id))
      .map((list) => ({ name: list.name, type: list.type })) || []
)
const excludedLists = computed(
  () =>
    props.selectedAudience?.availableLists
      .filter((list) => props.selectedAudience?.excludedLists.includes(list.id))
      .map((list) => ({ name: list.name, type: list.type })) || []
)
</script>

<template>
  <div class="rounded-md border bg-white">
    <div class="border-b border-border p-4">
      <slot name="title">
        <p class="text-base font-semibold text-foreground">
          {{ t('pages.audienceAside.title') }}
        </p>
      </slot>
    </div>

    <div class="p-3.5 flex flex-col gap-4">
      <div class="rounded-lg bg-muted px-6 py-4 flex items-center gap-6">
        <img class="w-auto h-8" src="/images/campaign/target.svg" alt="target" />

        <div class="flex flex-col">
          <p class="text-sm font-medium text-foreground">
            {{ t('pages.audienceAside.recipients', (targetsCount || 0) > 1 ? 2 : 1) }}
          </p>

          <div class="flex items-center gap-2">
            <span v-if="loading" class="inline-flex items-center text-sm text-muted-foreground pl-1">
              <svg
                class="mr-2 h-4 w-4 animate-spin text-muted-foreground"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {{ t('pages.campaign.campaignCreatePage.summary.calculationProgress') }}
            </span>

            <span v-else class="text-lg font-bold text-foreground">{{ formatNumber(targetsCount || 0) }}</span>
          </div>
          <div
            v-if="showExplanations && (storesContactsCount || 0) > 0 && (storesCount || 0) > 0"
            class="flex items-center text-xs text-foreground-secondary mt-2 gap-1"
          >
            <span
              >{{
                t(
                  'pages.audienceAside.recipientsBreakdownStores',
                  {
                    audienceCount: formatNumber(storesContactsCount || 0)
                  },
                  { plural: (storesContactsCount || 0) > 1 ? 2 : 1 }
                )
              }}
            </span>
            <AppTooltip
              class="inline-flex"
              :text="t('pages.audienceAside.recipientsTooltip')"
              tooltip-position="bottom"
              width="w-72"
            >
              <Info class="size-4 cursor-pointer text-foreground-secondary" />
            </AppTooltip>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div v-if="stores.length > 0" class="flex flex-col gap-2">
          <h3 class="text-sm font-medium">{{ t('pages.campaign.campaignTargetBlock.categories.location') }}</h3>
          <div class="flex flex-wrap items-center gap-2">
            <BaseChip :text="t('global.multiselect.stores.storesCount', { count: stores.length }, stores.length)">
              <template #leading>
                <Store />
              </template>
            </BaseChip>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <h3 class="text-sm font-medium">{{ t('pages.campaign.campaignTargetBlock.categories.segments') }}</h3>
          <div class="flex flex-col gap-2 pl-2">
            <div class="flex flex-col gap-1">
              <h4 class="text-foreground-secondary text-xs">
                {{ t('pages.audienceAside.included') }}
              </h4>
              <div class="flex flex-wrap items-center gap-2">
                <template v-if="availableLists.length > 0">
                  <BaseChip
                    v-for="available in availableLists"
                    :key="available.name"
                    :text="available.name"
                    :color="getAudienceColor(available.type)"
                  />
                </template>
                <BaseChip v-else :text="t('pages.campaign.campaignTargetBlock.segments.allCustomers.email')" />
              </div>
            </div>

            <template v-if="excludedLists.length > 0">
              <div class="flex flex-col gap-1">
                <h4 class="text-foreground-secondary text-xs">
                  {{ t('pages.audienceAside.excluded') }}
                </h4>
                <div class="flex flex-wrap items-center gap-2">
                  <BaseChip
                    v-for="excluded in excludedLists"
                    :key="excluded.name"
                    :text="excluded.name"
                    :color="getAudienceColor(excluded.type)"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
